/**
 * Savings plans slideshow banner for the landing page.
 *
 * One full-width hero slide per enabled plan. Swipeable on mobile with
 * optional auto-advance when more than one plan is shown.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Spinner } from '../../components/ui'
import type { PublicSavingsPlan } from '../../lib/savingsPlans'

const AUTO_ADVANCE_MS = 6000

function PlanSlideContent({ plan, preview = false }: { plan: PublicSavingsPlan; preview?: boolean }) {
  const { t } = useTranslation()

  const ctaClass =
    'landing-cta-primary tap-target rounded-lg px-4 py-2 text-sm font-semibold text-white'

  return (
    <>
      <div
        aria-hidden="true"
        className="landing-hero-orb -top-6 -right-4 size-32 bg-gold-500/25"
        style={{ animationDelay: '0s' }}
      />
      <div
        aria-hidden="true"
        className="landing-hero-orb bottom-0 -left-8 size-36 bg-brand-400/20"
        style={{ animationDelay: '2s' }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-brand-900/95 via-brand-800/90 to-brand-600/80"
      />

      <div className="relative flex flex-col gap-3">
        <h3 className="text-xl font-bold text-white sm:text-2xl">{plan.title}</h3>
        <div
          aria-hidden="true"
          className="h-0.5 w-12 origin-left rounded-full bg-gold-400"
        />
        <p className="text-sm leading-relaxed text-brand-100 sm:text-base">{plan.tagline}</p>
        <p className="text-xs font-medium text-gold-300 sm:text-sm">
          {t('savingsPlans.flexibleSaving')}
        </p>
        <div className="mt-1">
          {preview ? (
            <div className={ctaClass}>{t('savingsPlans.applyCta')}</div>
          ) : (
            <Link to={`/plans/${plan.id}`} className={ctaClass}>
              {t('savingsPlans.applyCta')}
            </Link>
          )}
        </div>
      </div>
    </>
  )
}

function PlanSlide({
  plan,
  hidden,
  preview = false,
}: {
  plan: PublicSavingsPlan
  hidden?: boolean
  preview?: boolean
}) {
  return (
    <div
      className="savings-plans-slide relative bg-brand-800 sm:rounded-[--radius-card]"
      aria-hidden={hidden}
    >
      <div className="relative min-h-[12rem] px-4 py-8 sm:px-8">
        <PlanSlideContent plan={plan} preview={preview} />
      </div>
    </div>
  )
}

export function SavingsPlansBanner({
  plans,
  loading,
  preview = false,
}: {
  plans: PublicSavingsPlan[]
  loading: boolean
  preview?: boolean
}) {
  const { t } = useTranslation()
  const trackRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const userInteractedRef = useRef(false)
  const scrollSyncRef = useRef(false)

  const planOrderKey = plans.map((p) => p.id).join(',')
  const isCarousel = plans.length > 1

  const scrollToIndex = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      const track = trackRef.current
      if (!track || plans.length === 0) return
      const clamped = ((index % plans.length) + plans.length) % plans.length
      scrollSyncRef.current = true
      track.scrollTo({ left: clamped * track.clientWidth, behavior })
      setActiveIndex(clamped)
    },
    [plans.length],
  )

  const markUserInteraction = useCallback(() => {
    userInteractedRef.current = true
  }, [])

  useEffect(() => {
    setActiveIndex(0)
    userInteractedRef.current = false
    const track = trackRef.current
    if (track && isCarousel) {
      scrollSyncRef.current = true
      track.scrollTo({ left: 0, behavior: 'instant' })
    }
  }, [planOrderKey, isCarousel])

  useEffect(() => {
    const track = trackRef.current
    if (!track || !isCarousel) return

    function syncFromScroll() {
      if (scrollSyncRef.current) {
        scrollSyncRef.current = false
        return
      }
      const width = track!.clientWidth
      if (width <= 0) return
      const index = Math.round(track!.scrollLeft / width)
      setActiveIndex(Math.min(index, plans.length - 1))
    }

    track.addEventListener('scroll', syncFromScroll, { passive: true })
    return () => track.removeEventListener('scroll', syncFromScroll)
  }, [isCarousel, plans.length])

  useEffect(() => {
    if (preview || !isCarousel || userInteractedRef.current || paused) return

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReduced) return

    const id = window.setInterval(() => {
      scrollToIndex(activeIndex + 1)
    }, AUTO_ADVANCE_MS)

    return () => window.clearInterval(id)
  }, [activeIndex, isCarousel, paused, preview, scrollToIndex])

  if (plans.length === 0 && !loading) return null

  return (
    <div
      role="region"
      aria-label={t('savingsPlans.bannerLabel')}
      className="relative -mx-4 sm:mx-0"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {isCarousel ? (
        <div
          ref={trackRef}
          className="savings-plans-banner"
          onTouchStart={markUserInteraction}
        >
          {plans.map((plan, index) => (
            <PlanSlide key={plan.id} plan={plan} hidden={index !== activeIndex} preview={preview} />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden sm:rounded-[--radius-card]">
          {plans[0] && <PlanSlide plan={plans[0]} preview={preview} />}
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center rounded-[--radius-card] bg-brand-900/40">
          <Spinner label={t('common.loading')} />
        </div>
      )}
    </div>
  )
}
